import {API_BASE_URL, STORAGE_KEYS} from '@/shared/config'
import type {
  AuthResponse,
  ForgotPasswordDto,
  LoginDto,
  MessageResponse,
  RegisterDto,
  RegisterResponse,
  ResetPasswordDto,
} from '../model/types'

const wrongEmailOrPassword = 'Неверный email или пароль'
const sessionExpired = 'Сессия истекла. Войдите снова.'

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'User with this email not found': wrongEmailOrPassword,
  'This email already exists': 'Пользователь с таким email уже зарегистрирован',
  'invalid or expired token': 'Ссылка недействительна или истекла',
  'invalid token purpose': 'Неверная ссылка',
  'invalid token subject': 'Неверная ссылка',
  'Email is not verified': 'Email не подтверждён. Проверьте почту.',
  'Missing refresh token': sessionExpired,
  'Invalid refresh token': sessionExpired,
  'Refresh token revoked': sessionExpired,
  'Refresh token expired': sessionExpired,
  'email is required': 'Введите email',
  'new password is required': 'Введите новый пароль',
  'invalid or expired reset token': 'Ссылка для сброса пароля недействительна или истекла',
}

function getAuthErrorMessage(status: number, apiMessage: string): string {
  const normalized = (apiMessage || '').trim()
  if (AUTH_ERROR_MESSAGES[normalized]) return AUTH_ERROR_MESSAGES[normalized]
  if (status === 401) return wrongEmailOrPassword
  if (status === 403) return 'Доступ запрещён'
  if (status === 404) return 'Не найдено'
  if (status >= 500) return 'Ошибка сервера. Попробуйте позже.'
  if (status >= 400) return 'Произошла ошибка. Попробуйте позже.'
  return 'Произошла ошибка. Попробуйте позже.'
}

function getToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
}

let refreshPromise: Promise<AuthResponse> | null = null

async function doRefresh(): Promise<AuthResponse> {
  const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) {
    const body = await res.text()
    let msg = body
    try {
      const j = JSON.parse(body)
      msg = j.error || j.message || body
    } catch {
      msg = body
    }
    throw new Error(getAuthErrorMessage(res.status, msg))
  }
  return res.json() as Promise<AuthResponse>
}

async function request<T>(
  path: string,
  options: RequestInit & {token?: string | null; skipRetry?: boolean} = {},
): Promise<T> {
  const {token = getToken(), skipRetry, ...init} = options
  const headers = new Headers(init.headers as HeadersInit)
  headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })

  if (res.status === 401 && !skipRetry && token) {
    try {
      if (!refreshPromise) refreshPromise = doRefresh()
      const {access_token} = await refreshPromise
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, access_token)

      const retryHeaders = new Headers(init.headers as HeadersInit)
      retryHeaders.set('Content-Type', 'application/json')
      retryHeaders.set('Authorization', `Bearer ${access_token}`)
      const retryRes = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers: retryHeaders,
        credentials: 'include',
      })
      if (!retryRes.ok) {
        const body = await retryRes.text()
        let apiMessage = body
        try {
          const j = JSON.parse(body)
          apiMessage = j.error || j.message || body
        } catch {
          apiMessage = body
        }
        throw new Error(getAuthErrorMessage(retryRes.status, apiMessage))
      }
      if (retryRes.status === 204) return undefined as T
      return retryRes.json() as Promise<T>
    } catch (refreshErr) {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
      throw refreshErr
    } finally {
      refreshPromise = null
    }
  }

  if (!res.ok) {
    const body = await res.text()
    let apiMessage = body
    try {
      const j = JSON.parse(body)
      if (j.error) apiMessage = j.error
      else if (j.message) apiMessage = j.message
    } catch {
      apiMessage = body
    }
    throw new Error(getAuthErrorMessage(res.status, apiMessage || ''))
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export async function login(dto: LoginDto): Promise<AuthResponse> {
  const data = await request<AuthResponse>('/login', {
    method: 'POST',
    body: JSON.stringify(dto),
    token: null,
    skipRetry: true,
  })
  if (data.access_token) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.access_token)
  }
  return data
}

export async function register(dto: RegisterDto): Promise<RegisterResponse> {
  return request<RegisterResponse>('/register', {
    method: 'POST',
    body: JSON.stringify(dto),
    token: null,
    skipRetry: true,
  })
}

export async function createSession(verifyToken: string): Promise<AuthResponse> {
  const data = await request<AuthResponse>(
    `/auth/session?verify_token=${encodeURIComponent(verifyToken)}`,
    {token: null, skipRetry: true},
  )
  if (data.access_token) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.access_token)
  }
  return data
}

export async function refreshToken(): Promise<AuthResponse> {
  if (!refreshPromise) refreshPromise = doRefresh()
  try {
    const data = await refreshPromise
    if (data.access_token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.access_token)
    }
    return data
  } finally {
    refreshPromise = null
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  } catch {
    // server unreachable — still clear local state
  }
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
}

export async function forgotPassword(dto: ForgotPasswordDto): Promise<MessageResponse> {
  return request<MessageResponse>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(dto),
    token: null,
    skipRetry: true,
  })
}

export async function resetPassword(dto: ResetPasswordDto): Promise<MessageResponse> {
  return request<MessageResponse>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(dto),
    token: null,
    skipRetry: true,
  })
}
